import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Calculate as RecomputeIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import { useTableSort } from '../hooks/useTableSort';
import usePersistedFilters from '../hooks/usePersistedFilters';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import { formatDateTime } from '../utils/dateUtils';
import { EntityDialog, EMPTY_ENTITY_OPTIONS, getEntityErrorMessage } from '../components/Entities/EntityDialog';

const SEARCH_DEBOUNCE_MS = 400;
const ENTITIES_FILTER_DEFAULTS = {
  search: '', type: '', status: '', criticality: '', sort: '-createdAt', rowsPerPage: 20,
};

// Entity statuses are not part of the configurable case/detection status
// colours, so fall back to these when the settings lookup returns the default.
const entityStatusColors = {
  Active: '#4caf50',
  Inactive: '#757575',
  Compromised: '#d32f2f',
  'Under Investigation': '#ff9800',
  Quarantined: '#1976d2',
};

export const getRiskColor = (score) => {
  if (score >= 80) return '#d32f2f'; // Red
  if (score >= 60) return '#ff9800'; // Orange
  if (score >= 40) return '#ffc107'; // Yellow
  return '#4caf50'; // Green
};

const StatCard = ({ label, value, color }) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ color }}>{value}</Typography>
    </CardContent>
  </Card>
);

export const Entities = () => {
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor } = useSettings();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const canCreate = hasPermission('entities', 'create');
  const canUpdate = hasPermission('entities', 'update');
  const canDelete = hasPermission('entities', 'delete');

  const { filters, setFilter, reset: resetFilters } = usePersistedFilters('entities', ENTITIES_FILTER_DEFAULTS);

  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(EMPTY_ENTITY_OPTIONS);
  const [stats, setStats] = useState(null);

  // Filters (server-side)
  const searchTerm = filters.search;
  const setSearchTerm = (value) => setFilter('search', value);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm.trim());
  const typeFilter = filters.type;
  const setTypeFilter = (value) => setFilter('type', value);
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilter('status', value);
  const criticalityFilter = filters.criticality;
  const setCriticalityFilter = (value) => setFilter('criticality', value);
  const [page, setPage] = useState(0); // zero-based for TablePagination, not persisted
  const rowsPerPage = filters.rowsPerPage;
  const setRowsPerPage = (value) => setFilter('rowsPerPage', value);
  const { sort, field: sortField, direction: sortDirection, toggle: toggleSort, setSort } = useTableSort(filters.sort);

  // Keep the sort control and the persisted filters in sync in both
  // directions: adopt filters.sort when it changes externally (profile
  // hydration, "Clear filters"), otherwise persist local sort changes
  // made via column headers.
  const lastSyncedSort = useRef(filters.sort);
  useEffect(() => {
    if (sort === filters.sort) {
      lastSyncedSort.current = sort;
      return;
    }
    if (filters.sort !== lastSyncedSort.current) {
      lastSyncedSort.current = filters.sort;
      setSort(filters.sort);
    } else {
      lastSyncedSort.current = sort;
      setFilter('sort', sort);
    }
  }, [sort, filters.sort, setFilter, setSort]);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [syncingEndpoints, setSyncingEndpoints] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const statusColorFor = (status) => {
    const fromSettings = getStatusColor(status);
    const main = typeof fromSettings === 'string' ? fromSettings : fromSettings?.main;
    if (main && main !== '#757575') return main;
    return entityStatusColors[status] || '#757575';
  };

  // Debounce the search box
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Any filter change resets to the first page
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, typeFilter, statusFilter, criticalityFilter, rowsPerPage, sort]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.entities.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch entity stats:', err);
    }
  }, []);

  useEffect(() => {
    apiService.entities.getOptions()
      .then((res) => setOptions({ ...EMPTY_ENTITY_OPTIONS, ...res.data }))
      .catch((err) => console.error('Failed to load entity options:', err));
    fetchStats();
  }, [fetchStats]);

  const fetchEntities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: page + 1, limit: rowsPerPage, sort };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (criticalityFilter) params.criticality = criticalityFilter;

      const response = await apiService.entities.getAll(params);
      setEntities(response.data.entities || []);
      setTotal(response.data.pagination?.total ?? (response.data.entities || []).length);
    } catch (err) {
      console.error('Failed to fetch entities:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load entities. Please try again.');
      } else {
        setEntities([]);
        setTotal(0);
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sort, debouncedSearch, typeFilter, statusFilter, criticalityFilter]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const formatEntityDateTime = (dateString) => formatDateTime(dateString, user?.preferences);

  const hasFilters = Boolean(debouncedSearch || typeFilter || statusFilter || criticalityFilter);

  const clearFilters = () => {
    resetFilters();
    setDebouncedSearch('');
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (entity) => {
    setEditing(entity);
    setDialogOpen(true);
  };

  const handleSaved = (saved) => {
    notify(editing ? 'Entity updated' : 'Entity created');
    fetchEntities();
    fetchStats();
    if (!editing && saved?._id) navigate(`/dashboard/entities/${saved._id}`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiService.entities.delete(deleteTarget._id);
      notify('Entity deleted');
      setDeleteTarget(null);
      fetchEntities();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete entity:', err);
      notify(getEntityErrorMessage(err, 'Failed to delete entity'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const overview = stats?.overview || {};
  const highRiskCount = Array.isArray(stats?.highRiskEntities) ? stats.highRiskEntities.length : 0;
  const compromisedCount = (stats?.byStatus || []).find((s) => s._id === 'Compromised')?.count || 0;
  const avgRisk = Math.round(overview.averageRiskScore || 0);

  const handleSyncEndpoints = async () => {
    try {
      setSyncingEndpoints(true);
      const res = await apiService.entities.syncEndpoints();
      const d = res.data || {};
      const failed = (d.agents || []).filter((a) => a.error);
      notify(`EDR sync: ${d.hostsCreated ?? 0} hosts created, ${d.hostsUpdated ?? 0} updated, ${d.usersLinked ?? 0} user links${failed.length ? `; ${failed.map((a) => `${a.agentName}: ${a.error}`).join('; ')}` : ''}`, failed.length ? 'warning' : 'success');
      await Promise.all([fetchEntities(), fetchStats && fetchStats()]);
    } catch (err) {
      notify(`EDR sync failed: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setSyncingEndpoints(false);
    }
  };

  const handleRecomputeRisk = async () => {
    try {
      setRecomputing(true);
      const response = await apiService.entities.recomputeRisk();
      const { entities: entityCount = 0, updated = 0 } = response.data || {};
      notify(`Recomputed risk for ${updated} of ${entityCount} entities`);
      fetchEntities();
      fetchStats();
    } catch (err) {
      console.error('Failed to recompute risk:', err);
      notify(getEntityErrorMessage(err, 'Failed to recompute risk'), 'error');
    } finally {
      setRecomputing(false);
    }
  };

  const handleExportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ type: typeFilter, status: statusFilter, criticality: criticalityFilter }).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    try {
      await apiService.download(`/entities/export${qs ? `?${qs}` : ''}`, `entities-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Entities
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage assets and entities in your environment
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={syncingEndpoints ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncEndpoints}
              disabled={syncingEndpoints}
            >
              Sync from EDR
            </Button>
          )}
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={recomputing ? <CircularProgress size={16} /> : <RecomputeIcon />}
              onClick={handleRecomputeRisk}
              disabled={recomputing}
            >
              Recompute risk
            </Button>
          )}
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Entity
            </Button>
          )}
        </Box>
      </Box>

      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <StatCard label="Total Entities" value={overview.total ?? 0} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Average Risk Score" value={avgRisk} color={getRiskColor(avgRisk)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="High Risk (70+)" value={highRiskCount} color={highRiskCount > 0 ? 'error.main' : undefined} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Compromised" value={compromisedCount} color={compromisedCount > 0 ? 'error.main' : undefined} />
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={fetchEntities} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search name, identifier or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="">All Types</MenuItem>
                  {options.types.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {options.statuses.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Criticality</InputLabel>
                <Select value={criticalityFilter} label="Criticality" onChange={(e) => setCriticalityFilter(e.target.value)}>
                  <MenuItem value="">All Criticality Levels</MenuItem>
                  {options.criticalities.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Entities Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell field="type" label="Type" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="name" label="Name" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="identifier" label="Identifier" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="criticality" label="Criticality" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="status" label="Status" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="riskScore" label="Risk Score" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="owner" label="Owner" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="incidentCount" label="Incidents" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="createdAt" label="Created" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading entities...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : entities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography variant="h6" gutterBottom>
                        {hasFilters ? 'No entities match your filters' : 'No entities yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {hasFilters
                          ? 'Try adjusting or clearing your search and filters.'
                          : 'Track hosts, accounts, applications and other assets in your environment.'}
                      </Typography>
                      {hasFilters ? (
                        <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>
                      ) : canCreate ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Create your first entity
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : (
                  entities.map((entity) => {
                    const riskScore = Number(entity.riskScore) || 0;
                    const incidentCount = entity.incidents?.length || 0;
                    const owner = entity.owner || entity.assetInfo?.owner || '';
                    return (
                      <TableRow key={entity._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {entity.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {entity.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {entity.identifier}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entity.criticality || 'Unknown'}
                            size="small"
                            sx={{
                              backgroundColor: getSeverityColor(entity.criticality).main,
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entity.status || 'Unknown'}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: statusColorFor(entity.status),
                              color: statusColorFor(entity.status),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 60 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.max(0, riskScore))}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getRiskColor(riskScore),
                                  },
                                }}
                              />
                            </Box>
                            <Typography variant="body2" fontWeight="medium">
                              {riskScore}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{owner || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={incidentCount}
                            size="small"
                            variant="outlined"
                            color={incidentCount > 0 ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatEntityDateTime(entity.createdAt)}</TableCell>
                        <TableCell>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => navigate(`/dashboard/entities/${entity._id}`)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {canUpdate && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(entity)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(entity)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </CardContent>
      </Card>

      <EntityDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        entity={editing}
        options={options}
        onSaved={handleSaved}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete Entity</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>{' '}
            (<Box component="span" fontFamily="monospace">{deleteTarget?.identifier}</Box>)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Entities;
